<template>
  <div class="add-cards">
    <page-header title="Verify Cards">
      <template #actions>
        <align small-gap>
          <icon-button
            v-if="isAdmin && selectedCardIds.length > 0 && status !== 'active'"
            icon="cloud_upload"
            tooltip="Admit selected cards"
            :loading="admitting"
            @click="admitCards"
          />

          <template v-if="showActions">
            <icon-button
              v-if=" status !== 'active'"
              icon="drive_file_move"
              :tooltip="`Move selected cards ${status === 'review' ? 'back to in progress' : 'into ready for review'}`"
              :loading="moving"
              @click="moveCards"
            />

            <icon-button
              v-if=" status !== 'active'"
              icon="move_to_inbox"
              tooltip="Rescrape sales for selected cards"
              :loading="scraping"
              @click="scrapeSales"
            />

            <icon-button
              icon="refresh"
              tooltip="Refresh stats for selected cards"
              :loading="refreshing"
              @click="refreshStats"
            />

            <icon-button
              v-if="isAdmin"
              icon="delete"
              tooltip="Delete selected cards"
              :loading="deleting"
              @click="deleteCards"
            />
          </template>

          <icon-button
            icon="add_circle"
            tooltip="Add New Card"
            @click="addCard"
          />
        </align>
      </template>
    </page-header>

    <align flex collapse-end direction="flex-start">
      <shared-filters
        show-recent-searches
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <align small-gap>
        <pill-dropdown
          v-model="employee"
          :options="employeeFilterOptions"
        />

        <pill-dropdown
          v-model="status"
          :options="statusOptions"
        />

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      is-filtering
      :filters="filters"
      :results="numResults"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="rowing"
        title="You are all caught up!"
      />

      <template>
        <div class="list">
          <align v-show="cards.length" class="select-all">
            <icon-button
              :class="{ success: selectedCardIds.length === cards.length }"
              :icon="selectAllIcon"
              @click="toggleSelectAll"
            />

            <div><strong>{{ selectedCardIds.length }}</strong> selected</div>
          </align>

          <card-list-item
            v-for="card in cards"
            :key="card.id"
            as="router-link"
            :to="{ query: { ...$route.query, cardId: card.id } }"
            :card="card"
            :fields="itemFields"
            selectable
            :selected="selectedCardIds.includes(card.id)"
            @toggleSelected="toggleSelectedCard(card.id)"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import firebase from 'firebase/compat/app';
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';
import debounce from 'lodash.debounce';

import { DEFAULT_ADD_CARD_FILTERS, ADD_CARD_SORT_OPTIONS } from '@cardladder/shared/constants';

import { cardService, searchService } from '@/services';

import CardListItem from '@cardladder/shared/components/CardListItem';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';

export default {
  components: {
    CardListItem,
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
  },

  data() {
    return {
      limit: 50,
      loading: false,
      admitting: false,
      refreshing: false,
      scraping: false,
      moving: false,
      deleting: false,
      loaded: false,
      cards: [],
      page: 1,
      hasMoreData: true,
      numResults: 0,
      sortOptions: ADD_CARD_SORT_OPTIONS,
      defaultFilters: DEFAULT_ADD_CARD_FILTERS,
      selectedCardIds: [],
      statusOptions: [
        { value: 'review', label: 'Review' },
        { value: 'progress', label: 'Progress' },
        { value: 'active', label: 'Active' },
      ],
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  mounted() {
    this.$bus.$on('add-cards-changed', this.reset);
  },

  beforeDestroy() {
    this.$bus.$off('add-cards-changed', this.reset);
  },

  watch: {
    query() {
      this.reset();
    },

    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      employeeOptions: (state) => state.employeeOptions,
    }),

    ...mapGetters({
      isAdmin: 'isAdmin',
    }),

    employeeFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...this.employeeOptions,
      ];
    },

    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.addCardSort);

        if (activeSort) {
          return this.$store.state.addCardSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_ADD_CARD_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.addCardDirection;
      },

      set(value) {
        this.$store.commit('SET_ADD_CARD_DIRECTION', value);
      },
    },

    employee: {
      get() {
        return this.$store.state.addCardFilterMap.employee;
      },

      set(value) {
        this.filters = {
          ...this.filters,
          employee: value,
        };
      },
    },

    status: {
      get() {
        return this.$store.state.addCardFilterMap.status;
      },

      set(value) {
        this.filters = {
          ...this.filters,
          status: value,
        };
      },
    },

    filters: {
      get() {
        return this.$store.state.addCardFilterMap;
      },

      set(value) {
        this.$store.commit('SET_ADD_CARD_FILTERS', value);
      },
    },

    query: {
      get() {
        return this.$store.state.addCardQuery;
      },

      set(value) {
        this.$store.commit('SET_ADD_CARD_QUERY', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.cards.length === 0;
    },

    showActions() {
      return this.selectedCardIds.length > 0 && (this.isAdmin || this.employee === this.user.uid);
    },

    itemFields() {
      const dynamicField = (this.sort === 'currentValue' || this.sort === 'numSales')
        ? 'dateAdded'
        : this.sort;

      return [dynamicField, 'numSales', 'currentValue'];
    },

    selectAllIcon() {
      if (this.selectedCardIds.length === 0) return 'check_box_outline_blank';

      if (this.selectedCardIds.length === this.cards.length) return 'check_box';

      return 'indeterminate_check_box';
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;
      this.selectedCardIds = [];

      this.search(true);
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const filters = {
          ...this.filters,
          hidden: true,
        };

        if (this.status === 'active') {
          filters.hidden = false;
          delete filters.status;
        }

        const data = await searchService.search({
          index: 'cards',
          query: this.query,
          filters,
          page: this.page,
          limit: this.limit,
          sort: this.sort,
          direction: this.direction,
          includeHidden: this.status !== 'active',
        });

        this.numResults = data.totalHits;

        if (reset) {
          this.cards = data.hits;
        } else {
          this.cards = [...this.cards, ...data.hits];
        }

        if (data.hits.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    async admitCards() {
      if (window.confirm('Are you sure you want to admit these to the database?')) {
        try {
          this.admitting = true;

          await cardService.admitCardsInReview(this.selectedCardIds);
        } catch (e) {
          this.$toasted.error('Error admitting these cards');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.admitting = false;
          this.$toasted.success('Cards have been admitted to the database!');
          this.reset();
        }, 4000);
      }
    },

    async moveCards() {
      if (window.confirm('Are you sure you want to move these cards?')) {
        try {
          this.moving = true;

          await cardService.updateCardStatuses(this.selectedCardIds, this.status === 'review' ? 'progress' : 'review');
        } catch (e) {
          this.$toasted.error('Error moving these cards');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.moving = false;
          this.$toasted.success('Cards have been moved!');
          this.reset();
        }, 4000);
      }
    },

    async deleteCards() {
      if (window.confirm('Are you sure you want to delete these cards?')) {
        try {
          this.deleting = true;

          await cardService.deleteCards(this.selectedCardIds);
        } catch (e) {
          this.$toasted.error('Error deleting these cards');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.deleting = false;
          this.$toasted.success('Cards have been deleted!');
          this.reset();
        }, 4000);
      }
    },

    async refreshStats() {
      if (window.confirm('Are you sure you want to refresh the card stats?')) {
        this.refreshing = true;

        try {
          await Promise.all(this.selectedCardIds.map((id) => firebase.firestore().collection('normalizeSales').doc(id).set({ start: true })));
          await Promise.all(this.selectedCardIds.map((id) => firebase.functions().httpsCallable('httpSyncCardStats')({ card: id, uid: this.user.uid })));
        } catch (e) {
          this.$toasted.error('Error fetching stats for these cards');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.refreshing = false;
          this.$toasted.success('Card stats have been updated!');
          this.reset();
        }, 4000);
      }
    },

    async scrapeSales() {
      if (window.confirm('Are you sure you want to rescrape all data for these cards? Keep in mind this will delete ALL existing sales.')) {
        this.scraping = true;

        try {
          await Promise.all(this.selectedCardIds.map((id) => firebase.functions().httpsCallable('httpInitialSalesScrape')({ card: id, uid: this.user.uid })));
        } catch (e) {
          this.$toasted.error('Error scraping sales for these cards');
          Sentry.captureException(e);
        }

        this.scraping = false;
        this.$toasted.success('Card sales scrapes have started!');
        this.reset();
      }
    },

    nextPage() {
      this.page += 1;
    },

    addCard() {
      this.$bus.$emit('admin-edit-card');
    },

    clearFilters() {
      this.filters = {
        ...clone(this.defaultFilters),
        employee: this.filters.employee,
        status: this.filters.status,
      };
    },

    toggleSelectedCard(cardId) {
      if (this.selectedCardIds.indexOf(cardId) >= 0) {
        this.selectedCardIds.splice(this.selectedCardIds.indexOf(cardId), 1);
      } else {
        this.selectedCardIds.push(cardId);
      }
    },

    toggleSelectAll() {
      if (this.selectedCardIds.length === this.cards.length) {
        this.selectedCardIds = [];
      } else {
        this.selectedCardIds = [...this.cards.map(({ id }) => id)];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.add-cards {
  @include container;
  min-height: 100%;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}

.select-all {
  margin: 0 0 $gp $gp;
}
</style>
